// src/features/inventory/components/PriceAdjustmentDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { TrendingDown as TrendingDownIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { valuationAPI } from '../services/valuation';
import { parseUnitCost } from './ReceiptCostFields';
import { useApi } from '../../../hooks/api/useApi';
import { formatDKK } from '../../../utils/formatters/currencyFormatter';

// Same as the server: whole øre, half up. Worked in integer øre and hundredths
// of a percent, since 195 * 1.045 in floats is 203.77499… and would round down.
const newPrice = (price, mode, change) => {
  const ore = Math.round(price * 100);
  const result = mode === 'percent'
    ? Math.round((ore * Math.round((100 + change) * 100)) / 10000)
    : ore + Math.round(change * 100);
  return result / 100;
};

/**
 * Changes the restocking price of every active item in the chosen product
 * groups at once, by a percentage or an amount, e.g. for a supplier's yearly
 * price increase. Shows what each item's price becomes before saving.
 */
export default function PriceAdjustmentDialog({ open, onClose, onSuccess, productGroups, initialGroupId = '' }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading: saving, error, execute, clearError } = useApi();
  const { loading: loadingItems, execute: executeItems } = useApi();
  const [items, setItems] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [direction, setDirection] = useState(1);
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!open) return;
    setGroupIds(initialGroupId ? [initialGroupId] : []);
    setDirection(1);
    setMode('percent');
    setValue('');
    clearError();
    executeItems(() => valuationAPI.getItems()).then((result) => {
      if (result.success) setItems(result.data.data.filter((item) => item.active));
    });
  }, [open, initialGroupId, executeItems, clearError]);

  const itemCount = useMemo(() => {
    const counts = {};
    items.forEach((item) => { counts[item.product_group_id] = (counts[item.product_group_id] ?? 0) + 1; });
    return counts;
  }, [items]);

  const parsed = parseUnitCost(value);
  const change = parsed != null && parsed > 0 ? direction * parsed : null;
  const tooLow = change != null && mode === 'percent' && change <= -100;
  const tooHigh = change != null && mode === 'percent' && change > 1000;
  const valueError = value !== '' && (change == null || tooLow || tooHigh);

  const preview = useMemo(() => {
    const selected = new Set(groupIds);
    const rows = items.filter((item) => selected.has(item.product_group_id));
    const priced = rows.filter((item) => item.cost_price != null);
    const changed = change == null || valueError ? [] : priced
      .map((item) => ({ ...item, new_price: newPrice(item.cost_price, mode, change) }))
      .filter((item) => item.new_price !== item.cost_price);
    return {
      changed: changed.filter((item) => item.new_price >= 0),
      belowZero: changed.filter((item) => item.new_price < 0).length,
      withoutPrice: rows.length - priced.length,
    };
  }, [items, groupIds, mode, change, valueError]);

  const allSelected = productGroups.length > 0 && groupIds.length === productGroups.length;
  const toggleGroup = (groupId) => setGroupIds((prev) => (
    prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
  ));

  const isValid = groupIds.length > 0 && change != null && !valueError && preview.changed.length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    const result = await execute(() => valuationAPI.adjustCostPrices({ productGroupIds: groupIds, mode, value: change }));
    if (result.success) { onSuccess(result.data.data); onClose(); }
  };

  return (
    // A fixed height, so the dialog does not jump as the preview grows
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { height: 'min(860px, calc(100% - 64px))' } } }}
    >
      <DialogTitle>{t('inventory:items.price_adjustment.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('inventory:items.price_adjustment.description')}
          </Typography>

          {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">{t('inventory:items.price_adjustment.groups')}</Typography>
              <Button
                size="small"
                onClick={() => setGroupIds(allSelected ? [] : productGroups.map((g) => g.product_group_id))}
                disabled={saving}
              >
                {allSelected ? t('inventory:items.price_adjustment.select_none') : t('inventory:items.price_adjustment.select_all')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 2,
                maxHeight: 220,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                px: 3,
                py: 1,
              }}
            >
              {productGroups.map((group) => (
                <FormControlLabel
                  key={group.product_group_id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={groupIds.includes(group.product_group_id)}
                      onChange={() => toggleGroup(group.product_group_id)}
                      disabled={saving}
                    />
                  )}
                  label={(
                    <Box component="span" sx={{ display: 'inline-flex', gap: 1 }}>
                      {group.name}
                      <Typography component="span" variant="body2" color="text.secondary">
                        ({itemCount[group.product_group_id] ?? 0})
                      </Typography>
                    </Box>
                  )}
                  slotProps={{ typography: { variant: 'body2', noWrap: true } }}
                  sx={{ minWidth: 0, mr: 0 }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('inventory:items.price_adjustment.change')}</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={direction}
                onChange={(_, next) => next != null && setDirection(next)}
                disabled={saving}
              >
                <ToggleButton value={1}>
                  <TrendingUpIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('inventory:items.price_adjustment.raise')}
                </ToggleButton>
                <ToggleButton value={-1}>
                  <TrendingDownIcon fontSize="small" sx={{ mr: 1 }} />
                  {t('inventory:items.price_adjustment.lower')}
                </ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                value={value}
                onChange={(e) => setValue(e.target.value.replace('-', ''))}
                placeholder={mode === 'percent' ? '4,5' : '10,00'}
                disabled={saving}
                autoFocus
                error={valueError}
                helperText={valueError
                  ? (tooLow || tooHigh
                    ? t('inventory:items.price_adjustment.percent_range')
                    : t('inventory:items.receipt_cost.invalid'))
                  : ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {mode === 'percent' ? '%' : t('inventory:items.currency_suffix')}
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { inputMode: 'decimal' },
                }}
                sx={{ width: 140 }}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_, next) => next != null && setMode(next)}
                disabled={saving}
              >
                <ToggleButton value="percent">%</ToggleButton>
                <ToggleButton value="amount">{t('inventory:items.currency_suffix')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {mode === 'amount' && (
              <Typography variant="caption" color="text.secondary">
                {t('inventory:items.price_adjustment.amount_hint')}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('inventory:items.price_adjustment.preview')}</Typography>
            {loadingItems ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : (
              <>
                <Typography variant="body2" color={preview.changed.length ? 'text.primary' : 'text.secondary'}>
                  {groupIds.length === 0
                    ? t('inventory:items.price_adjustment.pick_groups')
                    : t('inventory:items.price_adjustment.will_change', { count: preview.changed.length })}
                </Typography>
                {groupIds.length > 0 && preview.withoutPrice > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('inventory:items.price_adjustment.without_price', { count: preview.withoutPrice })}
                  </Typography>
                )}
                {preview.belowZero > 0 && (
                  <Typography variant="body2" color="warning.main">
                    {t('inventory:items.price_adjustment.below_zero', { count: preview.belowZero })}
                  </Typography>
                )}
                {preview.changed.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 260, overflow: 'auto' }}>
                    <Box
                      component="table"
                      sx={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.875rem',
                        fontVariantNumeric: 'tabular-nums',
                        '& th, & td': { py: 1, px: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'right' },
                        '& th:first-of-type, & td:first-of-type': { textAlign: 'left', pl: 0 },
                        '& th': { color: 'text.secondary', fontWeight: 500, position: 'sticky', top: 0, bgcolor: 'background.paper' },
                      }}
                    >
                      <thead>
                        <tr>
                          <th>{t('inventory:items.grid.name')}</th>
                          <th>{t('inventory:items.price_adjustment.now')}</th>
                          <th>{t('inventory:items.price_adjustment.new')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.changed.map((item) => (
                          <tr key={item.item_id}>
                            <td>{item.name}</td>
                            <td>{formatDKK(item.cost_price)}</td>
                            <td>{formatDKK(item.new_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('inventory:items.price_adjustment.stock_keeps_cost')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 6, pb: 4 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {t('inventory:items.price_adjustment.submit', { count: preview.changed.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
